import { useMemo, useRef, useState } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { FilePreview } from "shared/types/File"
import { ProjectImportError } from "shared/types/__generated__/graphql"

import { useCreateProject } from "entities/projects/api/useCreateProject"
import { useCreateProjectFromXlsx } from "entities/projects/api/useCreateProjectFromXlsx"
import { useXlsxUpload } from "entities/projects/api/useXlsxUpload"
import { useNavigateToProject } from "entities/projects/model/useNavigateToProject"
import { useProjectPhases } from "entities/projects/model/useProjectPhases"
import {
  ProjectFormData,
  ProjectFormState
} from "features/project-management/types/ProjectFormData"

import { NewProjectOverlayModel } from "../NewProjectOverlay"
import { NewProjectOverlayConnectorProps } from "../NewProjectOverlayConnector"

export function useNewProjectOverlayModel({
  payload
}: NewProjectOverlayConnectorProps): NewProjectOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { defaultValue, navigateAfterSubmit } = payload
  const { createProject } = useCreateProject()
  const { createProjectFromXlsx } = useCreateProjectFromXlsx()
  const { uploadXlsx } = useXlsxUpload()
  const navigateToProject = useNavigateToProject(null)

  const [xlsxUploadKey, setXlsxUploadKey] = useState<string | undefined>(
    undefined
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isXlsxUploading, setIsXlsxUploading] = useState(false)

  const createdIdRef = useRef<string | undefined>(undefined)

  const { projectPhases, isLoading: isLoadingProjectPhaseOptions } =
    useProjectPhases()
  const projectPhaseOptions = useMemo(
    () => projectPhases || [],
    [projectPhases]
  )

  const submitProject = useEvent((formData: ProjectFormData) => {
    const id = generateUUID()
    createdIdRef.current = id
    return createProject({ ...formData.project, id })
  })

  const { handleSubmit: handlePlainCreate, isSubmitting: isCreating } =
    useFormSubmit<ProjectFormData>({
      onSubmit: submitProject,
      onSuccess: () => {
        closeOverlay()
        if (navigateAfterSubmit && createdIdRef.current) {
          navigateToProject({ id: createdIdRef.current })
        }
      }
    })

  const handleNewProject = useEvent(
    async (
      formData: ProjectFormData
    ): Promise<{ errors?: ProjectImportError[] } | undefined> => {
      if (xlsxUploadKey) {
        const id = generateUUID()
        setIsSubmitting(true)
        const result = await createProjectFromXlsx({
          project: { ...formData.project, id },
          s3Key: xlsxUploadKey
        })
        setIsSubmitting(false)

        if (result.errors) {
          const importErrors = result.errors
            .map(e => e.data)
            .filter((e): e is ProjectImportError => e != null)
          return { errors: importErrors.length > 0 ? importErrors : undefined }
        }
        closeOverlay()
        if (navigateAfterSubmit) {
          navigateToProject({ id })
        }
      } else {
        handlePlainCreate(formData)
      }
      return undefined
    }
  )

  const defaultFormData = useMemo<ProjectFormState>(
    () => ({
      project: defaultValue
    }),
    [defaultValue]
  )

  const handleXlsxUpload = useEvent(async (file: FilePreview) => {
    setIsXlsxUploading(true)
    const result = await uploadXlsx(file)
    setIsXlsxUploading(false)
    if (result.data) {
      setXlsxUploadKey(result.data.s3Key)
    }
    return result
  })

  const handleXlsxDelete = useEvent(() => {
    setXlsxUploadKey(undefined)
  })

  return {
    defaultFormData,
    projectPhaseOptions,
    isLoadingProjectPhaseOptions,
    isSubmitting: isSubmitting || isXlsxUploading || isCreating,
    onCancel: closeOverlay,
    onSubmit: handleNewProject,
    onXlsxUpload: handleXlsxUpload,
    onXlsxDelete: handleXlsxDelete,
    xlsxExampleUrl: "/assets/schakonext-projekt-import-vorlage.xlsx"
  }
}
